import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { getTableActionsConfig } from '../../config/appConfig';
import { FileIcon } from '../ui/Icons';

// Kanban-style board table: groups rows by a categorical field
// Props:
// - columns: same shape as AdvancedTable
// - data: array of rows
// - groupByKey: string key in row used to group (e.g., 'statusId')
// - groupLookup: map id -> label
// - onCardDrop: ({ item, fromGroup, toGroup }) => void
// - actionsRenderer: (row) => ReactNode
// - onRowClick: (row) => void
// - columnOrder: optional array of group ids to order columns

const BoardTable = ({
  columns = [],
  data = [],
  groupByKey,
  groupLookup = {},
  columnOrder,
  onCardDrop,
  actionsRenderer,
  onRowClick,
  className,
  boardId = 'board-table',
  onFileCountClick
}) => {
  const { theme } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');
  const [dragItem, setDragItem] = useState(null); // { item, fromGroup }

  // Determine groups present in data
  const groups = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      const key = groupByKey ? row[groupByKey] : 'ungrouped';
      const label = groupByKey ? (groupLookup[key] ?? String(key ?? 'Unassigned')) : 'All';
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(row);
    });
    // Ensure all lookup groups exist even if empty
    Object.keys(groupLookup || {}).forEach(k => {
      if (!map.has(Number.isNaN(Number(k)) ? k : Number(k))) {
        const gKey = Number.isNaN(Number(k)) ? k : Number(k);
        map.set(gKey, { key: gKey, label: groupLookup[k], items: [] });
      }
    });
    const arr = Array.from(map.values());
    if (columnOrder && columnOrder.length) {
      arr.sort((a, b) => (columnOrder.indexOf(a.key) - columnOrder.indexOf(b.key)));
    }
    return arr;
  }, [data, groupByKey, groupLookup, columnOrder]);

  const handleDragStart = (item, fromGroup) => (e) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragItem({ item, fromGroup });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (toGroup) => (e) => {
    e.preventDefault();
    if (dragItem && onCardDrop) {
      onCardDrop({ item: dragItem.item, fromGroup: dragItem.fromGroup, toGroup });
    }
    setDragItem(null);
  };

  const contentColumns = columns.filter(c => c.key !== 'actions');

  return (
    <div className={cn('w-full h-full overflow-auto', className)}>
      <div className={cn('grid gap-3 p-3', 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4')}>
        {groups.map(group => (
          <div key={String(group.key)} className={cn('rounded-xl border', theme.border.light, theme.colors.surface, theme.shadow.md)} onDragOver={handleDragOver} onDrop={handleDrop(group.key)}>
            <div className={cn('px-3 py-2 border-b sticky top-0 z-10', theme.border.light, theme.colors.surface)}>
              <div className={cn('text-sm font-semibold', theme.text.primary)}>{group.label}</div>
              <div className={cn('text-xs', theme.text.muted)}>{group.items.length} item{group.items.length === 1 ? '' : 's'}</div>
            </div>
            <div className="p-3 space-y-3">
              {group.items.map((row, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={handleDragStart(row, group.key)}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={cn(
                    'rounded-lg border transition-all duration-200 hover:-translate-y-[1px] cursor-move',
                    theme.border.light, theme.colors.surface, theme.shadow.sm
                  )}
                >
                  <div className={cn('px-3 py-2 border-b', theme.border.light)}>
                    <div className={cn('text-sm font-semibold truncate', theme.text.primary)}>
                      {String(row[contentColumns[0]?.key] ?? '')}
                    </div>
                    {contentColumns[1] && (
                      <div className={cn('text-xs truncate mt-1', theme.text.secondary)}>
                        {String(row[contentColumns[1].key] ?? '')}
                      </div>
                    )}
                  </div>
                  <div className="px-3 py-2 grid grid-cols-2 gap-3">
                    {contentColumns.slice(2).map(col => (
                      <div key={col.key} className="min-w-0">
                        <div className={cn('text-[10px] uppercase tracking-wider mb-1', theme.text.muted)}>
                          {col.header || col.label || col.key}
                        </div>
                        <div className={cn('text-sm truncate', theme.text.primary)}>
                          {(() => {
                            const v = row[col.key];
                            if (col.cellRenderer === 'fileCount') {
                              return (
                                <div className="flex justify-center items-center gap-1">
                                  <FileIcon className="w-4 h-4 text-blue-500" />
                                  <span 
                                    className={`text-sm font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`} 
                                    title={`${v || 0} file(s) - Click to view/download`}
                                    onClick={() => {
                                      console.log('📁 File count clicked in BoardTable:', { value: v, row, hasHandler: !!onFileCountClick });
                                      if (onFileCountClick) {
                                        onFileCountClick(row, v || 0);
                                      } else {
                                        console.log('❌ No onFileCountClick handler provided to BoardTable');
                                      }
                                    }}
                                  >
                                    {v || 0}
                                  </span>
                                </div>
                              );
                            }
                            return String(v ?? '');
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                  {actionsRenderer && (
                    <div className="px-3 pb-3 pt-0" onClick={(e) => e.stopPropagation()}>
                      <div className={cn(
                        "flex items-center justify-end",
                        spacing,
                        actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                      )}>
                        {actionsRenderer(row)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BoardTable;
export { BoardTable };


